import React, { PropsWithChildren, useEffect, useState } from "react";
import { useToggle } from "react-use";

import { useSliderNavigation } from "../hook";

const Context = React.createContext<null | ReturnType<
  typeof useCreateSliderContext
>>(null);

type UseCreateContextProps = {
  mediaLength: number;
  initialIndex: number;
};

const useCreateSliderContext = ({
  mediaLength,
  initialIndex,
}: UseCreateContextProps) => {
  const [isZoomed, toggleIsZoomed] = useToggle(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(initialIndex);

  const { navigation } = useSliderNavigation({
    mediaLength: mediaLength ?? 0,
    setCurrentPhotoIndex,
  });

  useEffect(() => {
    toggleIsZoomed(false);
  }, [currentPhotoIndex, toggleIsZoomed]);

  return {
    isZoomed,
    toggleIsZoomed,

    currentPhotoIndex,
    setCurrentPhotoIndex,

    navigation,

    initialIndex,
  };
};

export const useSliderContext = () => {
  const context = React.useContext(Context);

  if (!context) {
    throw new Error(
      "useSliderContext must be used within a SliderContextProvider",
    );
  }

  return context;
};

export const SliderContextProvider: React.FC<
  PropsWithChildren & UseCreateContextProps
> = ({ children, mediaLength, initialIndex }) => {
  const contextValue = useCreateSliderContext({ mediaLength, initialIndex });

  return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};
